import type { AuthState } from '../core/ngrx/state/auth.state';
import { initialAuthState } from '../core/ngrx/state/auth.state';
import type { FilterState } from '../core/ngrx/state/filter.state';
import { initialFilterState } from '../core/ngrx/state/filter.state';
import type { Action, ActionReducer, ActionReducerMap, MetaReducer } from '@ngrx/store';
import * as auth from '../core/ngrx/reducer/auth.reducer';
import * as filter from '../core/ngrx/reducer/filter.reducer';
import * as profile from '../pages/my-profile/ngrx/reducer/profile.reducer';
import * as extension from '../core/ngrx/reducer/extension.reducer';
import * as paymentHistory from '../pages/plan/ngrx/reducer/payment-history.reducer';
import * as optOut from '../pages/opt-out/ngrx/reducer/opt-out.reducer';
import * as contactList from '../pages/lists/contacts/ngrx/reducer/contacts.reducer';

import * as notification from '../core/ngrx/reducer/notification.reducer';
import * as billing from '../pages/plan/ngrx/reducer/billing-info.reducer';
import type { ProfileState } from '../pages/my-profile/ngrx/state/profile.state';
import { initialProfileState } from '../pages/my-profile/ngrx/state/profile.state';
import { ApplicationStates } from './index.config';
import type { ExtensionState } from '../core/ngrx/state/extension.state';
import { initialExtensionState } from '../core/ngrx/state/extension.state';
import type { BillingInfoState } from '../pages/plan/ngrx/state/billing-info.state';
import { initialBillingState } from '../pages/plan/ngrx/state/billing-info.state';
import { LogoutAction } from '../core/ngrx/action/auth.action';
import type { PaymentHistoryState } from '../pages/plan/ngrx/state/payment-history.state';
import { initialPaymentHistoryState } from '../pages/plan/ngrx/state/payment-history.state';
import type { OptOutState } from '../pages/opt-out/ngrx/state/opt-out.state';
import { initialOptOutState } from '../pages/opt-out/ngrx/state/opt-out.state';
import type { NotificationState } from '../core/ngrx/state/notification.state';
import { initialNotificationState } from '../core/ngrx/state/notification.state';
import { initialContactListState } from '../pages/lists/contacts/ngrx/state/contacts.state';
import type { ContactsListState } from '../core/interfaces/contact.interface';
import type { CreditSlotsState } from '../pages/plan/ngrx/state/credit-slots.state';
import { initialCreditSlotsState } from '../pages/plan/ngrx/state/credit-slots.state';
import { CreditSlotsReducer } from '../pages/plan/ngrx/reducer/credit-slot.reducer';

export interface ApplicationState {
  [ApplicationStates.AUTH]: AuthState;
  [ApplicationStates.FILTER]: FilterState;
  [ApplicationStates.PROFILE]: ProfileState;
  [ApplicationStates.EXTENSION]: ExtensionState;
  [ApplicationStates.BILLING]: BillingInfoState;
  [ApplicationStates.CREDIT_SLOTS]: CreditSlotsState;
  [ApplicationStates.PAYMENT_HISTORY]: PaymentHistoryState;
  [ApplicationStates.OPT_OUT]: OptOutState;
  [ApplicationStates.CONTACT_LIST]: ContactsListState;
  [ApplicationStates.NOTIFICATION]: NotificationState;
}

export const initialApplicationState: ApplicationState = {
  [ApplicationStates.AUTH]: { ...initialAuthState },
  [ApplicationStates.FILTER]: { ...initialFilterState },
  [ApplicationStates.PROFILE]: { ...initialProfileState },
  [ApplicationStates.BILLING]: { ...initialBillingState },
  [ApplicationStates.EXTENSION]: { ...initialExtensionState },
  [ApplicationStates.CREDIT_SLOTS]: { ...initialCreditSlotsState },
  [ApplicationStates.PAYMENT_HISTORY]: { ...initialPaymentHistoryState },
  [ApplicationStates.OPT_OUT]: { ...initialOptOutState },
  [ApplicationStates.CONTACT_LIST]: { ...initialContactListState },
  [ApplicationStates.NOTIFICATION]: { ...initialNotificationState },
};

export const reducers: ActionReducerMap<ApplicationState> = {
  [ApplicationStates.AUTH]: auth.AuthReducer,
  [ApplicationStates.FILTER]: filter.FilterReducer,
  [ApplicationStates.PROFILE]: profile.ProfileReducer,
  [ApplicationStates.EXTENSION]: extension.ExtensionReducer,
  [ApplicationStates.BILLING]: billing.BillingInfoReducer,
  [ApplicationStates.CREDIT_SLOTS]: CreditSlotsReducer,
  [ApplicationStates.PAYMENT_HISTORY]: paymentHistory.PaymentHistoryReducer,
  [ApplicationStates.OPT_OUT]: optOut.OptOutReducer,
  [ApplicationStates.CONTACT_LIST]: contactList.ContactListReducer,
  [ApplicationStates.NOTIFICATION]: notification.NotificationReducer,
};

/**
 * metaReducer to clear application state on logouts
 */
export function clearState(reducer: ActionReducer<ApplicationState>): ActionReducer<ApplicationState> {
  return (state: ApplicationState | undefined, action: Action): ApplicationState => {
    if (action.type === LogoutAction.type) {
      state = {
        [ApplicationStates.AUTH]: { ...initialAuthState },
        [ApplicationStates.FILTER]: { ...initialFilterState },
        [ApplicationStates.PROFILE]: { ...initialProfileState },
        [ApplicationStates.EXTENSION]: { ...initialExtensionState },
        [ApplicationStates.BILLING]: { ...initialBillingState },
        [ApplicationStates.CREDIT_SLOTS]: { ...initialCreditSlotsState },
        [ApplicationStates.PAYMENT_HISTORY]: { ...initialPaymentHistoryState },
        [ApplicationStates.OPT_OUT]: { ...initialOptOutState },
        [ApplicationStates.CONTACT_LIST]: { ...initialContactListState },
        [ApplicationStates.NOTIFICATION]: { ...initialNotificationState },
      };
    }

    return reducer(state, action);
  };
}

export const metaReducers: MetaReducer<ApplicationState>[] = [clearState];
